import React, { useCallback, useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp, faCheck } from '@fortawesome/pro-solid-svg-icons'

import Button from '../../../components/button'
import CreditLimitEnquiryModal from '../creditLimitEnquiryModal'

import { creditLimit } from '../../actions'

import AnimatedNumber from '../animatedNumber'
import { formatNumber } from '../../helpers/commonHelpers'

const CreditLimit = (props) => {
  const {
    auctionUuid,
    biddingStatus,
    bids,
    currentSpend,
    isRegistered,
    listings,
    maxBids,
    maxSpend,
    resetCreditLimit,
    retrieveCreditLimit,
    soldLots,
    userUuid
  } = props
  const [isFixed, setIsFixed] = useState(false)
  const [expandDetails, setExpandDetails] = useState(false)
  const [enqueryModal, setEnqueryModal] = useState(false)
  const creditLimitRef = useRef(null)
  const detailsRef = useRef(null)
  const hasAuctionUuidBeenSet = useRef(false)
  const previousSpendRef = useRef(0)
  const auctionUuidReady = !!useSelector((state) => state.lots.listingsByAuctionByCategoryUuid?.[auctionUuid])
  const auctionNav = document.querySelector('#bidjs .auction-navigation')
  const originalOffsetTop = auctionNav ? auctionNav.offsetTop + auctionNav.clientHeight : null
  const activeListings = useSelector((state) => state.listingSales?.activeListings || [])
  const auctionEndDate = useSelector((state) => state.auctions?.auctions?.[auctionUuid]?.endsAt)
  const setRef = (el) => {
    if (el) {
      creditLimitRef.current = el
    }
  }

  const handleScroll = useCallback(() => {
    if (creditLimitRef) {
      const isFixed = window.scrollY >= originalOffsetTop
      setIsFixed(isFixed)
    }
  }, [originalOffsetTop])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  // Close expandDetails if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        detailsRef.current &&
        !detailsRef.current.contains(event.target) &&
        !creditLimitRef.current.contains(event.target)
      ) {
        setExpandDetails(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Reset currentSpend to 0 only once when auctionUuid is set
  useEffect(() => {
    if (auctionUuid && !hasAuctionUuidBeenSet.current) {
      resetCreditLimit()
      previousSpendRef.current = 0
      if (auctionUuidReady && isRegistered) {
        retrieveCreditLimit(auctionUuid)
        hasAuctionUuidBeenSet.current = true
      }
    }
    if (!auctionUuidReady) {
      hasAuctionUuidBeenSet.current = false
    }
  }, [auctionUuid, auctionUuidReady, resetCreditLimit, retrieveCreditLimit, isRegistered])

  // Update previousSpend only after loading is complete
  useEffect(() => {
    if (auctionUuid && currentSpend !== previousSpendRef.current) {
      previousSpendRef.current = currentSpend
    }
  }, [currentSpend, auctionUuid])

  // hide Credit Limit after midnight the day after auction ends if no active listings
  const midnight = new Date(auctionEndDate)
  midnight.setDate(midnight.getDate() + 1)
  midnight.setHours(0, 0, 0, 0)
  const hideCreditLimit = !activeListings.length && (new Date() > midnight)

  if (!userUuid || !auctionUuid || biddingStatus !== 'active' || !isRegistered || !userUuid || hideCreditLimit) {
    return null
  }

  const previousSpend = previousSpendRef.current || 0
  const currentSpendAmount = currentSpend || 0
  const statusClass = (currentSpendAmount && (maxSpend - currentSpendAmount) < 1000) ? 'danger' : 'info'
  const bidStartNumber = previousSpend || 0
  const bidEndNumber = currentSpendAmount ? currentSpendAmount.toLocaleString() : 0
  const className = 'blx-credit-limit'

  // TODO: Wrap in useEffect to update expandDetails in realtime
  const combinedBids = expandDetails
    ? Object.entries(bids).reduce((acc, [uuid, value]) => {
      const lotNumber = listings[uuid].lotNumber
      acc[lotNumber] = { bid: value, autoBid: null, isWon: null }
      return acc
    }, {})
    : {}

  if (expandDetails) {
    if (soldLots && Object.keys(soldLots).length) {
      Object.entries(soldLots).forEach(([uuid, value]) => {
        const lotNumber = listings[uuid].lotNumber
        if (!combinedBids?.[lotNumber]) {
          combinedBids[lotNumber] = { bid: value, autoBid: null, isWon: true }
        } else {
          combinedBids[lotNumber].isWon = true
        }
      })
    }
    Object.entries(maxBids).forEach(([uuid, value]) => {
      const lotNumber = listings[uuid].lotNumber
      if (!combinedBids?.[lotNumber]) {
        combinedBids[lotNumber] = { bid: null, autoBid: value, isWon: null }
      } else {
        combinedBids[lotNumber].autoBid = value
      }
    })
  }

  return (
    <div className={`${className}${isFixed ? ' fixed' : ''}`} ref={setRef}>
      <div className={`${className}--inner`}>
        <div className={`${className}--summary`}>
          <span className={`${className}--summary-label`}>Credit Limit</span>
          <div className={`${className}--status ${className}--status-${statusClass}`}>
            $<AnimatedNumber start={bidStartNumber} end={bidEndNumber} duration='1000' /> / ${maxSpend ? maxSpend.toLocaleString() : 0}
            <Button
              className={`${className}--details-btn`}
              onClick={() => setExpandDetails(!expandDetails)}
              aria-label='Toggle Bid Breakdown'
              >
              <FontAwesomeIcon icon={!expandDetails ? faChevronDown : faChevronUp} />
            </Button>
          </div>
        </div>
        <div className={`${className}--details ${!expandDetails ? 'collapse' : ''}`} ref={detailsRef}>
          <div className={`${className}--details-row`}>
            <div className={`${className}--details-heading`}><strong>Bid Breakdown</strong></div>
            <div className={`${className}--details-item`}>
              <strong>Lot No.</strong>
              <strong>Won</strong>
              <strong>Bids</strong>
              <strong>Auto Bids</strong>
            </div>
            {Object.entries(combinedBids).map(([lotNumber, bid]) => {
              return <div className={`${className}--details-item ${bid.isWon ? 'text-success' : ''}`} key={`bid-lot-number-${lotNumber}`}>
                <span>Lot {lotNumber}</span>
                <span>{!!bid.isWon && <FontAwesomeIcon icon={faCheck} />}</span>
                <span className={bid.autoBid ? 'text-muted' : ''}>{!!bid.bid && `$${formatNumber(bid.bid)}`}</span>
                <span>{bid.autoBid ? `$${formatNumber(bid.autoBid)}` : '-'}</span>
              </div>
            })}
            <div className={`${className}--details-total`}><span>Total</span> <span>${formatNumber(currentSpend)}</span></div>
            <div className={`${className}--details-disclaimer`}><span>*Auto Bids are counted towards the Credit Limit</span></div>
            <div className={`${className}--details-enquiry-wrapper`}>
              <Button
                className={`${className}--enquiry-btn btn-link`}
                size='xs'
                onClick={() => setEnqueryModal(true)}
                aria-label='Increase Credit Limit'
                >
                Request Credit Limit increase
              </Button>
            </div>
          </div>
        </div>
      </div>
      {enqueryModal && (
        <CreditLimitEnquiryModal
          auctionUuid={auctionUuid}
          onClose={() => setEnqueryModal(false)}
          />
      )}
    </div>
  )
}

CreditLimit.propTypes = {
  auctionUuid: PropTypes.string,
  biddingStatus: PropTypes.string,
  bids: PropTypes.object,
  currentSpend: PropTypes.number,
  isRegistered: PropTypes.bool.isRequired,
  listings: PropTypes.object,
  maxBids: PropTypes.object,
  maxSpend: PropTypes.number,
  resetCreditLimit: PropTypes.func.isRequired,
  retrieveCreditLimit: PropTypes.func.isRequired,
  soldLots: PropTypes.object,
  userUuid: PropTypes.string
}

function mapStateToProps (state) {
  return {
    biddingStatus: state.creditLimit.biddingStatus,
    bids: state.creditLimit.bids,
    currentSpend: state.creditLimit.currentSpend,
    listings: state.lots.listings,
    maxBids: state.creditLimit.maxBids,
    maxSpend: state.creditLimit.maxSpend,
    soldLots: state.creditLimit.soldLots,
    userUuid: state.users.user.uuid
  }
}

function mapDispatchToProps (dispatch) {
  return {
    resetCreditLimit: () => dispatch(creditLimit.reset()),
    retrieveCreditLimit: (auctionUuid) => dispatch(creditLimit.retrieve(auctionUuid))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CreditLimit)
